import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  CheckCircle, 
  Clock, 
  XCircle,
  ArrowLeft,
  Filter,
  Download,
  Calendar,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AdminPaymentsPage = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalPaid: 0,
    totalPending: 0,
    totalCommission: 0,
    tutorShare: 0,
    companyShare: 0
  });

  useEffect(() => {
    fetchPayments();
  }, [filterStatus]);

  const fetchPayments = async () => {
    try {
      const url = filterStatus === 'all' 
        ? `${BACKEND_URL}/api/admin/payments`
        : `${BACKEND_URL}/api/admin/payments?status=${filterStatus}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setPayments(data.payments || []);
        calculateStats(data.payments || []);
      }
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (paymentsData) => {
    const totalRevenue = paymentsData.reduce((sum, p) => sum + (p.amountAUD || 0), 0);
    const paidPayments = paymentsData.filter(p => p.status === 'Paid');
    const totalPaid = paidPayments.reduce((sum, p) => sum + (p.amountAUD || 0), 0);
    const totalPending = paymentsData.filter(p => p.status === 'Pending').length;
    const tutorShare = paidPayments.reduce((sum, p) => sum + (p.tutorShare || 0), 0);
    const companyShare = paidPayments.reduce((sum, p) => sum + (p.companyShare || 0), 0);

    setStats({
      totalRevenue,
      totalPaid,
      totalPending,
      totalCommission: tutorShare + companyShare,
      tutorShare,
      companyShare
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Paid': { color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)', icon: CheckCircle },
      'Pending': { color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)', icon: Clock },
      'Failed': { color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)', icon: XCircle },
      'Refunded': { color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.1)', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig['Pending'];
    const Icon = config.icon;

    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
        style={{ color: config.color, backgroundColor: config.bgColor }}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" 
            style={{ borderColor: 'var(--accent-teal)' }}></div>
          <p style={{ color: 'var(--text-grey)' }}>Loading payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Header */}
      <div className="premium-gradient text-white py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin/applications')}
                className="bg-white/20 hover:bg-white/30 backdrop-blur"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin
              </Button>
              <h1 className="heading-lg">Payment Management</h1>
            </div>
            <Button
              onClick={() => navigate('/admin/migrations')}
              className="bg-white/20 hover:bg-white/30 backdrop-blur"
            >
              Migrations Panel
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="premium-card bg-white/10 backdrop-blur border-white/20 text-white p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs opacity-80">Total Revenue</p>
                  <p className="text-xl font-bold">${stats.totalRevenue.toFixed(2)}</p>
                </div>
              </div>
            </Card>

            <Card className="premium-card bg-white/10 backdrop-blur border-white/20 text-white p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs opacity-80">Paid</p>
                  <p className="text-xl font-bold">${stats.totalPaid.toFixed(2)}</p>
                </div>
              </div>
            </Card>

            <Card className="premium-card bg-white/10 backdrop-blur border-white/20 text-white p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs opacity-80">Company Share</p>
                  <p className="text-xl font-bold">${stats.companyShare.toFixed(2)}</p>
                </div>
              </div>
            </Card>

            <Card className="premium-card bg-white/10 backdrop-blur border-white/20 text-white p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Award className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs opacity-80">Tutor Share</p>
                  <p className="text-xl font-bold">${stats.tutorShare.toFixed(2)}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Filters & Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5" style={{ color: 'var(--text-grey)' }} />
            <span className="text-sm font-medium">Filter:</span>
            <div className="flex gap-2">
              {['all', 'Paid', 'Pending', 'Failed'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    filterStatus === status
                      ? 'text-white'
                      : 'bg-[var(--bg-light)]'
                  }`}
                  style={filterStatus === status ? { backgroundColor: 'var(--accent-teal)' } : {}}
                >
                  {status === 'all' ? 'All' : status}
                </button>
              ))}
            </div>
          </div>

          <Button className="premium-button-secondary flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Report
          </Button>
        </div>

        {/* Payments Table */}
        {payments.length === 0 ? (
          <Card className="premium-card p-12 text-center">
            <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <h3 className="heading-md mb-2">No Payments Found</h3>
            <p style={{ color: 'var(--text-grey)' }}>
              {filterStatus === 'all' 
                ? 'No payments have been processed yet.'
                : `No ${filterStatus} payments found.`}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {payments.map((payment) => (
              <Card key={payment.id} className="premium-card p-6 hover:shadow-lg transition-shadow">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Left: Payment Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg mb-1">{payment.studentName}</h3>
                        <p className="text-sm" style={{ color: 'var(--text-grey)' }}>
                          {payment.studentEmail}
                        </p>
                      </div>
                      {getStatusBadge(payment.status)}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-xs mb-1" style={{ color: 'var(--text-grey)' }}>Package</p>
                        <p className="font-medium">{payment.packageName}</p>
                      </div>
                      <div>
                        <p className="text-xs mb-1" style={{ color: 'var(--text-grey)' }}>Lessons</p>
                        <p className="font-medium">
                          {payment.lessonsUsed || 0} / {payment.lessonsIncluded}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs mb-1" style={{ color: 'var(--text-grey)' }}>Tutor</p>
                        <p className="font-medium">{payment.assignedTutorName || 'Not assigned'}</p>
                      </div>
                      <div>
                        <p className="text-xs mb-1" style={{ color: 'var(--text-grey)' }}>Date</p>
                        <p className="font-medium text-xs">
                          {formatDate(payment.paidAt || payment.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Right: Financial Info */}
                  <div className="md:text-right md:min-w-[200px]">
                    <div className="mb-3">
                      <p className="text-sm mb-1" style={{ color: 'var(--text-grey)' }}>Total Amount</p>
                      <p className="text-2xl font-bold" style={{ color: 'var(--accent-teal)' }}>
                        ${payment.amountAUD.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <div>
                        <p style={{ color: 'var(--text-grey)' }}>Tutor (60%)</p>
                        <p className="font-semibold">${payment.tutorShare?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div>
                        <p style={{ color: 'var(--text-grey)' }}>Company (40%)</p>
                        <p className="font-semibold">${payment.companyShare?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stripe Session ID (for debugging) */}
                {payment.stripeCheckoutSessionId && (
                  <div className="mt-4 pt-4 border-t text-xs" style={{ borderColor: 'var(--bg-light)' }}>
                    <span style={{ color: 'var(--text-grey)' }}>Stripe Session: </span>
                    <code className="bg-[var(--bg-light)] px-2 py-1 rounded">
                      {payment.stripeCheckoutSessionId}
                    </code>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Commission Summary */}
        {payments.length > 0 && (
          <Card className="premium-card p-6 mt-8">
            <h3 className="heading-md mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6" style={{ color: 'var(--accent-gold)' }} />
              Commission Breakdown
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-light)' }}>
                <p className="text-sm mb-2" style={{ color: 'var(--text-grey)' }}>Total Paid Revenue</p>
                <p className="text-3xl font-bold" style={{ color: 'var(--accent-teal)' }}>
                  ${stats.totalPaid.toFixed(2)}
                </p>
              </div>
              <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'rgba(20, 184, 166, 0.05)' }}>
                <p className="text-sm mb-2" style={{ color: 'var(--text-grey)' }}>Tutor Earnings (60%)</p>
                <p className="text-3xl font-bold" style={{ color: 'var(--accent-teal)' }}>
                  ${stats.tutorShare.toFixed(2)}
                </p>
              </div>
              <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'rgba(212, 175, 55, 0.05)' }}>
                <p className="text-sm mb-2" style={{ color: 'var(--text-grey)' }}>Company Revenue (40%)</p>
                <p className="text-3xl font-bold" style={{ color: 'var(--accent-gold)' }}>
                  ${stats.companyShare.toFixed(2)}
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminPaymentsPage;
